import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import cloneDeep from 'lodash/cloneDeep';
import { Observable, of, throwError } from 'rxjs';
import { apiEndpoints } from 'src/app/api-endpoints';
import { Store, select } from '@ngrx/store';
import { BoardState } from '../board/+store/board-state/board-state.reducer';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { FilterBar,  FilterBars } from './models/filter-bar.model';
import { selectFilterBarList } from '../board/+store/board-state/board-state.selectors';
import { clearFilterBars, deleteFilterBarSuccess, loadFilterBarSuccess, updateFilterBarSuccess } from '../board/+store/board-state/board-state.actions';

@Injectable({
  providedIn: 'root'
})
export class FilterBarService {
  private apiUrl = environment.apiConfig.baseUri + apiEndpoints.filterBar;
  private filterBarApis = {
    createFilterBar: '/create-filter-bar',
    updateFilterBar: '/update-filter-bar',
    deleteFilterBar: '/delete-filter-bar',
    getFilterBars: '/get-filter-bars',
  };

  constructor(
    private http: HttpClient,
    private boardStore: Store<BoardState>
  ) {}

  /**
   * Retrieves the filterBars for the current user.
   *
   * - If the FilterBarState already contains filters, returns them immediately.
   * - Otherwise, calls the backend API, dispatches an action to store the results, and returns the data.
   */
  getFilterBars(): Observable<FilterBar[]> {
    return this.boardStore.pipe(
      select(selectFilterBarList),
      take(1), // Take the current state once
      switchMap(filterBarList => {
        if (filterBarList && filterBarList.length > 0) {
          // Filters already exist in the store: return them as a Filter[].
          return of(filterBarList);
        } else {
          return this.http.post<FilterBars>(
            `${this.apiUrl}${this.filterBarApis.getFilterBars}`,
            {}
          ).pipe(
            filter(response => response !== null),
            map(filterBars => {
              const filterBarList: FilterBar[] = [];
              filterBars.filterBars.forEach((filterBar: FilterBar) => {
                filterBarList.push(filterBar);
              });
              return filterBarList;
            }),
            tap(filterBarList => {
              // Dispatch an action to store the filterBars in the state.
              this.boardStore.dispatch(loadFilterBarSuccess({ filterBars: cloneDeep(filterBarList) }));
            })
          );
        }
      })
    );
  }

  /**
   * Creates a new FilterBar in the backend.
   *
   * - Updates FilterBarState upon success.
   */
  createFilterBar(filterBar: FilterBar): Observable<FilterBar> {

    return this.http.post<FilterBar>(
      `${this.apiUrl}${this.filterBarApis.createFilterBar}`, filterBar).pipe(
      tap((modelResponse: FilterBar) => {
        // Dispatch an action to store the filter in the state.
        if (modelResponse) {
          this.boardStore.dispatch(updateFilterBarSuccess({ filterBar: modelResponse }));
        }
      })
    );
  }

  /**
   * Updates the FilterBar in the backend.
   *
   * - Updates FilterBarState upon success.
   */
  updateFilterBar(filterBar: FilterBar): Observable<FilterBar> {

    return this.http.post<FilterBar>(
      `${this.apiUrl}${this.filterBarApis.updateFilterBar}`, filterBar).pipe(
      tap((modelResponse: FilterBar) => {
        // Dispatch an action to store the filter in the state.
        if (modelResponse) {
          this.boardStore.dispatch(updateFilterBarSuccess({ filterBar: modelResponse }));
        }
      })
    );
  }

  /**
   * Deletes the FilterBar in the backend.
   *
   * - Updates FilterBarState upon success.
   */
  deleteFilterBar(filterBar: FilterBar): Observable<boolean> {
    const postObj = {
      filterBarId: filterBar.filterBarId
    };

    return this.http.post<boolean>(
      `${this.apiUrl}${this.filterBarApis.deleteFilterBar}`, postObj).pipe(
      tap((response: boolean) => {
        // Dispatch an action to delete the filterBar in the state.
        if (response) {
          this.boardStore.dispatch(deleteFilterBarSuccess({ filterBarId: postObj.filterBarId }));
        }
      })
    );
  }

  /**
   * Clears the filterBars stored in the FilterState.
   */
  clearFilterBars(): void {
    this.boardStore.dispatch(clearFilterBars());
  }
}

